import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';

const banks = [
    { name: 'UBS', color: '#E60000', textColor: '#E60000' },
    { name: 'Credit Suisse', color: '#003D6E', textColor: '#003D6E' },
    { name: 'Deutsche Bank', color: '#0018A8', textColor: '#0018A8' },
    { name: 'Santander', color: '#EC0000', textColor: '#EC0000' },
    { name: 'Raiffeisen', color: '#F5A623', textColor: '#E8941A' },
    { name: 'PostFinance', color: '#FFCC00', textColor: '#D4AA00' },
    { name: 'Commerzbank', color: '#FFD700', textColor: '#C4A200' },
    { name: 'BBVA', color: '#004481', textColor: '#004481' },
    { name: 'BNP Paribas', color: '#00915A', textColor: '#00915A' },
    { name: 'ING', color: '#FF6200', textColor: '#FF6200' },
    { name: 'Lloyds', color: '#006A4D', textColor: '#006A4D' },
    { name: 'Zürcher KB', color: '#0066B3', textColor: '#0066B3' },
];

const BankLogo = ({ bank }) => (
    <div className="flex-shrink-0 mx-3">
        <div 
            className="px-8 py-4 bg-white rounded-xl border-2 hover:shadow-lg transition-all duration-300 flex items-center justify-center min-w-[140px]"
            style={{ borderColor: bank.color }}
        >
            <span 
                className="text-base font-bold whitespace-nowrap"
                style={{ color: bank.textColor }}
            >
                {bank.name}
            </span>
        </div>
    </div>
);

export const BankPartners = () => {
    const { t } = useTranslation();

    return (
        <section className="py-16 lg:py-24 bg-background relative overflow-hidden" data-testid="bank-partners-section">
            <div className="absolute inset-0 bg-gradient-to-b from-muted/30 via-transparent to-muted/30" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                <div className="text-center mb-12">
                    <span className="inline-flex items-center gap-2 text-accent font-medium text-sm uppercase tracking-wider mb-4">
                        <Building2 className="w-4 h-4" />
                        {t('banks.badge')}
                    </span>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
                        {t('banks.title')}{' '}
                        <span className="text-gradient-gold">{t('banks.entities')}</span>
                    </h2>
                    <p className="text-muted-foreground max-w-2xl mx-auto">
                        {t('banks.description')}
                    </p>
                </div>

                {/* Bank Logos - Animated Rows */}
                <div className="relative mb-12">
                    <div className="overflow-hidden mb-4">
                        <div className="flex animate-scroll">
                            {[...banks, ...banks].map((bank, index) => (
                                <BankLogo key={`row1-${index}`} bank={bank} />
                            ))}
                        </div>
                    </div>

                    <div className="overflow-hidden">
                        <div className="flex animate-scroll-reverse">
                            {[...banks.slice().reverse(), ...banks.slice().reverse()].map((bank, index) => (
                                <BankLogo key={`row2-${index}`} bank={bank} />
                            ))}
                        </div>
                    </div>

                    <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-background to-transparent pointer-events-none z-10" />
                    <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    {[
                        { value: '40+', label: t('banks.partnerBanks') },
                        { value: '15+', label: t('banks.yearsExperience') },
                        { value: 'Europe', label: t('banks.coverage') },
                        { value: t('banks.noLimit'), label: t('banks.financingAmount') },
                    ].map((stat, index) => (
                        <div 
                            key={index}
                            className="text-center p-6 rounded-2xl bg-card border border-border hover:border-primary/30 hover:shadow-md transition-all duration-300"
                            data-testid={`bank-stat-${index}`}
                        >
                            <div className="font-display text-3xl font-bold text-primary mb-2">
                                {stat.value}
                            </div>
                            <div className="text-sm text-muted-foreground">
                                {stat.label}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <style>{`
                @keyframes scroll {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
                @keyframes scroll-reverse {
                    0% { transform: translateX(-50%); }
                    100% { transform: translateX(0); }
                }
                .animate-scroll { animation: scroll 30s linear infinite; }
                .animate-scroll-reverse { animation: scroll-reverse 30s linear infinite; }
                .animate-scroll:hover, .animate-scroll-reverse:hover { animation-play-state: paused; }
            `}</style>
        </section>
    );
};
